// Public pre-filled CLIENT INTERNAL AUDIT form (/ia/:token) — linked from the 6 / 9 / 11-month reminder emails.
// Everything known is pre-filled and editable; the latest external audit report upload is REQUIRED.
// Submit → updates the client + the internal audit cycle, creates a draft IA quotation, emails the client owner.
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { FirmHeader } from '../components/PublicComponents'
import { iaStandardOptions, standardsLabel } from '../lib/service'
import { emailHtml, emailDate } from '../lib/emailHtml'
import { oneLine } from '../lib/oneLine'
import { toast, failed } from '../lib/toast'

const box = { width: '100%', padding: '9px 11px', border: '0.5px solid rgba(0,0,0,0.22)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' }
const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 4 }}>{children}{required && <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>}</label>
)
const Centered = ({ icon, title, text }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', padding: 20 }}>
    <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 460, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.1)' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>{icon}</div>
      <h2 style={{ fontWeight: 500, marginBottom: 8 }}>{title}</h2>
      <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>{text}</p>
    </div>
  </div>
)

export default function IAForm() {
  const { token } = useParams()
  const [state, setState] = useState('loading')   // loading | ok | invalid | used | done
  const [f, setF] = useState({})
  const [upload, setUpload] = useState(null)       // { path, name }
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [contract, setContract] = useState(false)  // client on a multi-year contract → schedule, no new quote
  const [stdOpts, setStdOpts] = useState([])       // standard tick-boxes (list + any already on this client's record)

  useEffect(() => {
    supabase.rpc('get_ia_form_by_token', { p_token: token }).then(({ data, error }) => {
      if (error || !data) { setState('invalid'); return }
      if (data.status !== 'ok') { setState(data.status === 'used' ? 'used' : 'invalid'); return }
      setF({
        company_name: data.company_name || '', uen: data.uen || '', company_address: oneLine(data.company_address || ''),
        pic_name: data.pic_name || '', pic_email: data.pic_email || '', pic_phone: data.pic_phone || '',
        standards: data.standards || [], certification_body: data.certification_body || '',
        iso_cert_expiry: data.iso_cert_expiry || '', scope_of_certification: data.scope_of_certification || '',
        knows_next_external: '', next_external_audit_date: data.next_external_audit_date || '', remarks: '',
      })
      setStdOpts(iaStandardOptions(data.standards))
      setContract(!!data.contract)
      setState('ok')
    })
  }, [token])

  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  const toggleStd = s => setF(x => ({ ...x, standards: x.standards.includes(s) ? x.standards.filter(y => y !== s) : [...x.standards, s] }))

  async function pickFile(file) {
    if (!file) return
    if (!/\.pdf$/i.test(file.name)) { toast('Please upload a PDF', 'warning'); return }
    if (file.size > 20 * 1024 * 1024) { toast('File is over 20 MB', 'warning', { detail: 'Please compress it and try again' }); return }
    setUploading(true)
    try {
      const { data, error } = await supabase.functions.invoke('ia-public', { body: { action: 'upload-url', token, filename: file.name, size: file.size } })
      if (error || !data?.ok) throw new Error(data?.error || 'Could not prepare the upload.')
      const { error: upErr } = await supabase.storage.from('ia-client-uploads').uploadToSignedUrl(data.path, data.uploadToken, file, { contentType: 'application/pdf' })
      if (upErr) throw upErr
      setUpload({ path: data.path, name: file.name })
    } catch (e) { failed('Upload failed', e.message || e, 'Please try again') }
    setUploading(false)
  }

  async function submit() {
    const required = [['company_name', 'Company full name'], ['company_address', 'Company address'], ['pic_name', 'PIC name'], ['pic_email', 'PIC email'], ['pic_phone', 'PIC contact number']]
    const missing = required.filter(([k]) => !String(f[k] || '').trim()).map(([, l]) => l)
    if (!f.standards.length) missing.push('Standard(s)')
    if (!f.knows_next_external) missing.push('Whether you know your next external audit date')
    if (f.knows_next_external === 'Yes' && !f.next_external_audit_date) missing.push('Next external audit date')
    if (!upload) missing.push('Your latest external audit report')
    if (missing.length) { toast('Please complete the missing fields', 'warning', { detail: '• ' + missing.join('\n• ') }); return }
    setSubmitting(true)
    const payload = { ...f, company_address: oneLine(f.company_address).trim(), upload_path: upload.path, upload_name: upload.name }
    const { data, error } = await supabase.rpc('submit_ia_form', { p_token: token, p_payload: payload })
    if (error) { failed("We couldn't submit the form", error.message, 'Please try again'); setSubmitting(false); return }
    // Best-effort notifications (never block the client).
    const company = data?.company_name || f.company_name
    if (data?.contract) {
      // Contract year: the job is already created → owner schedules it; Accounts invoice this year's audit.
      try {
        await supabase.functions.invoke('send-internal-email', { body: {
          ...(data.owner_user_id ? { user_id: data.owner_user_id, cc: ['accounts@ccis.com.sg'] } : { to: 'accounts@ccis.com.sg' }),
          subject: `Questionnaire Submitted – ${company} (contract audit)`,
          html: emailHtml({
            title: 'Questionnaire submitted — contract audit', greeting: data.owner_user_id ? 'Dear {{staff_name}},' : 'Dear Accounts,',
            lines: ['A new internal audit questionnaire has been submitted. This audit is covered by the client’s contract, so the job has been created — please schedule the audit.',
              { html: '<strong style="color:#c0392b;">Accounts: please invoice this year’s audit.</strong>' }],
            details: [['Company', company], ['Audit type', 'Internal Audit'], ['Standard(s)', standardsLabel(data.standards || f.standards) || '—'],
              ['Next external audit', f.knows_next_external === 'Yes' ? emailDate(f.next_external_audit_date) : 'Not known'],
              ['Job', data.job_ref || '—'], ['Audits left after this one', String(data.audits_left ?? '—')],
              ['Contract until', data.contract_until ? emailDate(data.contract_until) : '—']],
            button: { label: 'Open job →', url: `${window.location.origin}/jobs/${data.job_id}` },
          }),
        } })
      } catch (e) { /* non-blocking */ }
    } else if (data?.owner_user_id) {
      try {
        await supabase.functions.invoke('send-internal-email', { body: {
          user_id: data.owner_user_id,
          // Same wording as the bizSAFE "Questionnaire Submitted" notice.
          subject: `Questionnaire Submitted – ${company}`,
          html: emailHtml({
            title: 'Questionnaire submitted', greeting: 'Dear {{staff_name}},',
            lines: ['A new internal audit questionnaire has been submitted. Please review the submission and prepare the quotation accordingly.'],
            details: [['Company', company], ['Audit type', 'Internal Audit'], ['Standard(s)', standardsLabel(data.standards || f.standards) || '—'],
              ['Next external audit', f.knows_next_external === 'Yes' ? emailDate(f.next_external_audit_date) : 'Not known'],
              ['Quotation reference', data.quote_ref || '—']],
            button: { label: 'Open Quotations →', url: `${window.location.origin}/quotations` },
          }),
        } })
      } catch (e) { /* non-blocking */ }
    }
    setSubmitting(false)
    setState('done')
  }

  if (state === 'loading') return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f6f3', color: '#999' }}>Loading…</div>
  if (state === 'invalid') return <Centered icon="🔗" title="Invalid link" text="This link is not valid. Please reply to our email or contact us at (65) 6376 0550." />
  if (state === 'used') return <Centered icon="✓" title="Already submitted" text="This form has already been submitted. We will be in touch with you shortly." />
  if (state === 'done') return <Centered icon="✓" title="Thank you" text={contract
    ? `We have received your details for ${f.company_name}. We will contact you to schedule your internal audit.`
    : `We have received your details for ${f.company_name}. We will prepare your quotation and send it to ${f.pic_email}.`} />

  const field = (k, label, opts = {}) => (
    <div style={{ marginBottom: 16 }}>
      <Label required={opts.required}>{label}</Label>
      {opts.textarea
        ? <textarea value={f[k] || ''} onChange={e => set(k, e.target.value)} style={{ ...box, minHeight: 70, resize: 'vertical' }} />
        : <input type={opts.type || 'text'} value={f[k] || ''} onChange={e => set(k, opts.oneLine ? oneLine(e.target.value) : e.target.value)} style={box} />}
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 20px 60px' }}>
      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        <FirmHeader accredited={false} />
        <h1 style={{ fontWeight: 500, fontSize: 20, marginBottom: 8 }}>Internal Audit — your details</h1>
        <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          We have filled in what we know. Please check the details, update anything that has changed, and upload your latest external audit report{contract ? ' so we can schedule your next internal audit under your contract' : ''}.
        </p>
        <div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', border: '0.5px solid rgba(0,0,0,0.1)' }}>
          {field('company_name', 'Company full name', { required: true })}
          {field('uen', 'Workplace No. / UEN No.')}
          {field('company_address', 'Company address', { required: true, oneLine: true })}
          {field('pic_name', 'PIC name', { required: true })}
          {field('pic_email', 'PIC email', { required: true, type: 'email' })}
          {field('pic_phone', 'PIC contact number', { required: true })}

          <div style={{ marginBottom: 16 }}>
            <Label required>Which standard(s) should this internal audit cover?</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {stdOpts.map(s => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.standards.includes(s)} onChange={() => toggleStd(s)} style={{ width: 'auto' }} /> {s}
                </label>
              ))}
            </div>
          </div>
          {field('certification_body', 'Certification body (auditing firm’s name)')}
          {field('iso_cert_expiry', 'Current certificate expiry', { type: 'date' })}
          {field('scope_of_certification', 'Scope of certification', { textarea: true })}

          <div style={{ marginBottom: 16 }}>
            <Label required>Do you know your next external audit date?</Label>
            <div style={{ display: 'flex', gap: 10 }}>
              {['Yes', 'No'].map(opt => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14,
                  border: `0.5px solid ${f.knows_next_external === opt ? '#006363' : 'rgba(0,0,0,0.2)'}`, background: f.knows_next_external === opt ? '#e0f2f2' : '#fff' }}>
                  <input type="radio" checked={f.knows_next_external === opt} onChange={() => set('knows_next_external', opt)} style={{ display: 'none' }} />{opt}
                </label>
              ))}
            </div>
          </div>
          {f.knows_next_external === 'Yes' && field('next_external_audit_date', 'Next external audit date', { required: true, type: 'date' })}

          <div style={{ marginBottom: 16 }}>
            <Label required>Latest external audit report from your certification body (PDF, max 20 MB)</Label>
            {upload
              ? <div style={{ fontSize: 13, color: '#27500a', background: '#eaf3de', padding: '8px 12px', borderRadius: 8 }}>✓ {upload.name} <button onClick={() => setUpload(null)} style={{ marginLeft: 10, fontSize: 12, border: 'none', background: 'none', color: '#006363', cursor: 'pointer', textDecoration: 'underline' }}>Replace</button></div>
              : <input type="file" accept="application/pdf,.pdf" disabled={uploading} onChange={e => pickFile(e.target.files[0])} style={{ fontSize: 13 }} />}
            {uploading && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Uploading…</div>}
          </div>
          {field('remarks', 'Any special requirements or notes for us', { textarea: true })}

          <button onClick={submit} disabled={submitting || uploading}
            style={{ marginTop: 8, width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none', background: '#006363', color: '#fff', fontWeight: 500, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}
